import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Building2, Search, MapPin, Users, Star, Briefcase, Globe, ArrowRight, Filter, ChevronRight, X, Phone, Mail, Linkedin, Facebook, Instagram, Twitter, Youtube, ExternalLink, Lock, ChevronLeft, MessageCircleDashed as MessageCircle, } from 'lucide-react'
import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'

interface Company {
  _id: string
  userId?: string
  tradeName?: string
  commercialName?: string
  displayName?: string
  legalName?: string
  rfc?: string
  industry?: string
  city?: string
  state?: string
  country?: string
  address?: string
  description?: string
  logoUrl?: string
  logoOffset?: { x: number; y: number }
  website?: string
  employeeCount?: string
  contactName?: string
  contactRole?: string
  phone?: string
  whatsapp?: string
  email?: string
  socialLinks?: Record<string, string>
}

interface Vacancy {
  _id: string
  title?: string
  area?: string
  modality?: string
  city?: string
  state?: string
  salaryMin?: number
  salaryMax?: number
  contractType?: string
  status?: string
}

const INDUSTRIES = [
  'Tecnología', 'Manufactura', 'Comercio', 'Educación', 'Salud',
  'Finanzas', 'Logística', 'Construcción', 'Servicios', 'Alimentos',
]

const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  linkedin: <Linkedin size={15} />,
  facebook: <Facebook size={15} />,
  instagram: <Instagram size={15} />,
  twitter: <Twitter size={15} />,
  x: <Twitter size={15} />,
  youtube: <Youtube size={15} />,
  whatsapp: <MessageCircle size={15} />,
}

function CompanyCard({
  company,
  onSelect,
  isAuthenticated,
}: {
  company: Company
  onSelect: (c: Company) => void
  isAuthenticated: boolean
}) {
  const navigate = useNavigate()
  const initials = (company.tradeName || company.commercialName || company.displayName || company.legalName || 'E')
    .split(' ')
    .slice(0, 2)
    .map((w: string) => w.charAt(0).toUpperCase())
    .join('')

  const handleClick = () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    onSelect(company)
  }

  return (
    <div
      onClick={handleClick}
      className="group flex cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-amber-300 hover:shadow-md"
    >
      {/* Logo / avatar */}
      <div className="flex items-start gap-4">
        {company.logoUrl ? (
          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border border-slate-100 bg-white">
            <img
              src={company.logoUrl}
              alt={company.tradeName}
              className="absolute inset-0 h-full w-full object-contain p-1"
              style={
                company.logoOffset
                  ? { objectPosition: `${50 + company.logoOffset.x}% ${50 + company.logoOffset.y}%` }
                  : undefined
              }
            />
          </div>
        ) : (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-lg font-bold text-amber-400">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-bold text-slate-900">
            {company.tradeName || company.commercialName || company.displayName || company.legalName || 'Empresa'}
          </h3>
          {company.industry && (
            <span className="mt-1 inline-block rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              {company.industry}
            </span>
          )}
        </div>
      </div>

      {/* Descripción */}
      {company.description && (
        <p className="mt-4 line-clamp-2 text-sm leading-relaxed text-slate-600">
          {company.description}
        </p>
      )}

      {/* Metadatos */}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-500">
        {(company.city || company.state) && (
          <span className="flex items-center gap-1">
            <MapPin size={12} />
            {[company.city, company.state].filter(Boolean).join(', ')}
          </span>
        )}
        {company.employeeCount && (
          <span className="flex items-center gap-1">
            <Users size={12} />
            {company.employeeCount} empleados
          </span>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between">
        {isAuthenticated ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 group-hover:underline">
            <Briefcase size={13} /> Ver detalle <ChevronRight size={12} />
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400">
            <Lock size={13} /> Inicia sesión para ver más
          </span>
        )}
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <Star size={12} className="text-amber-400" fill="currentColor" />
          <span>Perfil verificado</span>
        </div>
      </div>
    </div>
  )
}

function CompanyDetailPanel({
  company,
  onClose,
}: {
  company: Company
  onClose: () => void
}) {
  const [vacancies, setVacancies] = useState<Vacancy[]>([])
  const [loadingVac, setLoadingVac] = useState(true)

  useEffect(() => {
    if (!company.userId) {
      setLoadingVac(false)
      return
    }
    lumi.entities.vacancies
      .list({ filter: { companyUserId: company.userId, status: 'publicada' }, limit: 20 })
      .then((r) => setVacancies(r.list as Vacancy[]))
      .catch(() => {})
      .finally(() => setLoadingVac(false))
  }, [company.userId])

  const initials = (company.tradeName || company.commercialName || company.displayName || company.legalName || 'E')
    .split(' ')
    .slice(0, 2)
    .map((w: string) => w.charAt(0).toUpperCase())
    .join('')

  const socialEntries = Object.entries(company.socialLinks || {}).filter(([, v]) => v)

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-900 px-5 py-4">
        <div className="flex items-center gap-3">
          {company.logoUrl ? (
            <div className="relative h-10 w-10 overflow-hidden rounded-lg border border-slate-700 bg-white">
              <img
                src={company.logoUrl}
                alt={company.tradeName}
                className="absolute inset-0 h-full w-full object-contain p-0.5"
              />
            </div>
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-700 text-sm font-bold text-amber-400">
              {initials}
            </div>
          )}
          <div>
            <p className="font-bold text-white">
              {company.tradeName || company.commercialName || company.displayName || company.legalName || 'Empresa'}
            </p>
            {company.industry && (
              <p className="text-xs text-amber-400">{company.industry}</p>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-700 hover:text-white"
        >
          <X size={18} />
        </button>
      </div>

      {/* Contenido scrollable */}
      <div className="flex-1 overflow-y-auto p-5 space-y-6">

        {/* Descripción */}
        {company.description && (
          <div>
            <h4 className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">Sobre la empresa</h4>
            <p className="text-sm leading-relaxed text-slate-700">{company.description}</p>
          </div>
        )}

        {/* Datos generales */}
        <div>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Información general</h4>
          <div className="space-y-2 text-sm text-slate-700">
            {company.legalName && company.legalName !== (company.tradeName || company.commercialName || company.displayName) && (
              <div className="flex gap-2">
                <span className="w-28 shrink-0 font-medium text-slate-500">Razón social</span>
                <span>{company.legalName}</span>
              </div>
            )}
            {company.rfc && (
              <div className="flex gap-2">
                <span className="w-28 shrink-0 font-medium text-slate-500">RFC</span>
                <span>{company.rfc}</span>
              </div>
            )}
            {company.employeeCount && (
              <div className="flex gap-2">
                <span className="w-28 shrink-0 font-medium text-slate-500">Empleados</span>
                <span>{company.employeeCount}</span>
              </div>
            )}
            {(company.city || company.state || company.country) && (
              <div className="flex gap-2">
                <span className="w-28 shrink-0 font-medium text-slate-500">Ubicación</span>
                <span>{[company.address, company.city, company.state, company.country].filter(Boolean).join(', ')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Contacto */}
        {(company.contactName || company.phone || company.email || company.website) && (
          <div>
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Contacto</h4>
            <div className="space-y-2 text-sm text-slate-700">
              {company.contactName && (
                <div className="flex gap-2">
                  <span className="w-28 shrink-0 font-medium text-slate-500">Responsable</span>
                  <span>
                    {company.contactName}
                    {company.contactRole ? ` — ${company.contactRole}` : ''}
                  </span>
                </div>
              )}
              {company.phone && (
                <a
                  href={`tel:${company.phone}`}
                  className="flex items-center gap-2 text-amber-700 hover:underline"
                >
                  <Phone size={13} /> {company.phone}
                </a>
              )}
              {company.whatsapp && (
                <a
                  href={`https://wa.me/${company.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-green-700 hover:underline"
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-green-600 text-white text-xs font-bold" style={{fontSize:8}}>WA</span>
                  {company.whatsapp}
                </a>
              )}
              {company.email && (
                <a
                  href={`mailto:${company.email}`}
                  className="flex items-center gap-2 text-amber-700 hover:underline"
                >
                  <Mail size={13} /> {company.email}
                </a>
              )}
              {company.website && (
                <a
                  href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-amber-700 hover:underline"
                >
                  <Globe size={13} /> {company.website}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Redes sociales */}
        {socialEntries.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Redes sociales</h4>
            <div className="flex flex-wrap gap-2">
              {socialEntries.map(([network, url]) => (
                <a
                  key={network}
                  href={url.startsWith('http') ? url : `https://${url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-amber-300 hover:text-amber-700"
                >
                  {SOCIAL_ICONS[network.toLowerCase()] ?? <ExternalLink size={13} />}
                  {network.charAt(0).toUpperCase() + network.slice(1)}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Vacantes activas */}
        <div>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
            Vacantes publicadas
          </h4>
          {loadingVac ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />
              ))}
            </div>
          ) : vacancies.length === 0 ? (
            <p className="text-sm text-slate-500">Esta empresa no tiene vacantes activas en este momento.</p>
          ) : (
            <div className="space-y-2">
              {vacancies.map((v) => (
                <Link
                  key={v._id}
                  to="/colaborador/empleos"
                  className="flex items-start justify-between rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 transition hover:border-amber-200 hover:bg-amber-50"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{v.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {[v.area, v.modality, [v.city, v.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                    </p>
                    {(v.salaryMin || v.salaryMax) && (
                      <p className="mt-0.5 text-xs font-medium text-amber-700">
                        {v.salaryMin && v.salaryMax
                          ? `$${v.salaryMin.toLocaleString()} – $${v.salaryMax.toLocaleString()}`
                          : v.salaryMin
                          ? `Desde $${v.salaryMin.toLocaleString()}`
                          : `Hasta $${v.salaryMax!.toLocaleString()}`}
                      </p>
                    )}
                  </div>
                  <ChevronRight size={14} className="mt-1 shrink-0 text-slate-400" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer del panel */}
      <div className="border-t border-slate-200 bg-white p-4">
        <Link
          to="/colaborador/empleos"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-400 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
        >
          <Briefcase size={15} /> Explorar todas las vacantes
        </Link>
      </div>
    </div>
  )
}

function Empresas() {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Company[]>([])
  const [loadingData, setLoadingData] = useState(true)
  const [search, setSearch] = useState('')
  const [industry, setIndustry] = useState('')
  const [city, setCity] = useState('')
  const [selected, setSelected] = useState<Company | null>(null)

  useEffect(() => {
    // Siempre cargamos el directorio (público), pero el detalle requiere sesión
    const fetchCompanies = async () => {
      try {
        const res = await lumi.entities.companies.list({
          sort: { createdAt: -1 },
          limit: 100,
        })
        setCompanies(res.list as Company[])
      } catch (e) {
        console.error('[Empresas] error:', e)
      } finally {
        setLoadingData(false)
      }
    }
    fetchCompanies()
  }, [])

  const handleSelect = (company: Company) => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    setSelected(company)
  }

  const availableIndustries = useMemo(() => {
    const set = new Set<string>()
    companies.forEach((c) => c.industry && set.add(c.industry))
    return Array.from(set).sort()
  }, [companies])

  const filtered = useMemo(() => {
    return companies.filter((c) => {
      if (search) {
        const q = search.toLowerCase()
        const match =
          c.tradeName?.toLowerCase().includes(q) ||
          c.commercialName?.toLowerCase().includes(q) ||
          c.displayName?.toLowerCase().includes(q) ||
          c.legalName?.toLowerCase().includes(q) ||
          c.industry?.toLowerCase().includes(q) ||
          c.description?.toLowerCase().includes(q)
        if (!match) return false
      }
      if (industry && c.industry !== industry) return false
      if (city && !c.city?.toLowerCase().includes(city.toLowerCase())) return false
      return true
    })
  }, [companies, search, industry, city])

  const inputCls =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400'

  const showPanel = selected !== null && isAuthenticated

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />

      {/* Hero */}
      <section className="bg-slate-900 px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white sm:text-3xl">
                Directorio de empresas
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                Descubre empresas que contratan talento en Beeznz Work
              </p>
              {!isLoading && !isAuthenticated && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-300">
                  <Lock size={12} /> Inicia sesión para ver el detalle completo de cada empresa
                </p>
              )}
            </div>
            <Link
              to="/registro"
              className="inline-flex w-fit items-center gap-2 rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
            >
              Registrar mi empresa <ArrowRight size={16} />
            </Link>
          </div>

          {/* Buscador */}
          <div className="mt-6 flex items-center gap-2 rounded-xl bg-white p-2 shadow-lg">
            <Search size={18} className="ml-2 shrink-0 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Busca una empresa por nombre, giro o descripción…"
              className="flex-1 bg-transparent px-1 py-1.5 text-sm text-slate-900 outline-none"
            />
          </div>
        </div>
      </section>

      <main className="mx-auto flex w-full max-w-7xl flex-1 gap-6 px-4 py-8 sm:px-6 lg:px-8">
        {/* Filtros desktop */}
        <aside className="hidden w-60 shrink-0 lg:block">
          <div className="sticky top-20 rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Filter size={15} /> Filtros
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Ciudad</label>
                <input
                  className={inputCls}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Ej. Puebla"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">Giro empresarial</label>
                <select
                  className={inputCls}
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                >
                  <option value="">Todos</option>
                  {(availableIndustries.length > 0 ? availableIndustries : INDUSTRIES).map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
              {(search || industry || city) && (
                <button
                  onClick={() => { setSearch(''); setIndustry(''); setCity('') }}
                  className="w-full rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Resultados + Panel */}
        <div className="flex flex-1 gap-4 overflow-hidden">
          {/* Lista */}
          <div className={`flex-1 min-w-0 transition-all duration-300 ${showPanel ? 'hidden lg:block lg:max-w-[calc(100%-360px)]' : ''}`}>
            {/* Stats */}
            <div className="mb-5 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                {loadingData
                  ? 'Cargando empresas…'
                  : `${filtered.length} empresa${filtered.length !== 1 ? 's' : ''} encontrada${filtered.length !== 1 ? 's' : ''}`}
              </p>
              {!loadingData && companies.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <Building2 size={13} />
                  {companies.length} en la plataforma
                </div>
              )}
            </div>

            {loadingData ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-52 animate-pulse rounded-2xl bg-slate-100" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center">
                <Building2 size={32} className="mx-auto text-slate-300" />
                <p className="mt-3 font-semibold text-slate-700">
                  {companies.length === 0 ? 'Aún no hay empresas registradas' : 'Sin resultados para tu búsqueda'}
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  {companies.length === 0
                    ? 'Sé el primero en registrar tu empresa en Beeznz Work.'
                    : 'Intenta con otro nombre, giro o ciudad.'}
                </p>
                {companies.length === 0 && (
                  <Link
                    to="/registro"
                    className="mt-5 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
                  >
                    Registrar mi empresa <ChevronRight size={16} />
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                {filtered.map((company) => (
                  <CompanyCard
                    key={company._id}
                    company={company}
                    onSelect={handleSelect}
                    isAuthenticated={!isLoading && isAuthenticated}
                  />
                ))}
              </div>
            )}

            {/* CTA unirse */}
            {!loadingData && (
              <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
                <Building2 size={28} className="mx-auto text-amber-600" />
                <h3 className="mt-3 font-bold text-amber-900">¿Tu empresa no está aquí?</h3>
                <p className="mt-1.5 text-sm text-amber-700">
                  Registra tu empresa en Beeznz Work y publica vacantes para encontrar el talento que necesitas.
                </p>
                <Link
                  to="/registro"
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
                >
                  Registrar empresa gratis <ArrowRight size={16} />
                </Link>
              </div>
            )}
          </div>

          {/* Panel de detalle — sólo con sesión */}
          {showPanel && (
            <div className="w-full lg:w-[360px] shrink-0 rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden flex flex-col">
              {/* Botón volver en móvil */}
              <button
                onClick={() => setSelected(null)}
                className="flex items-center gap-1.5 px-4 pt-3 pb-1 text-xs font-medium text-slate-500 hover:text-slate-800 lg:hidden"
              >
                <ChevronLeft size={14} /> Volver al directorio
              </button>
              <CompanyDetailPanel company={selected} onClose={() => setSelected(null)} />
            </div>
          )}

          {/* CTA de login si no hay sesión y hay empresas (overlay informativo) */}
          {!isLoading && !isAuthenticated && !loadingData && filtered.length > 0 && (
            <div className="hidden lg:flex lg:w-[340px] shrink-0 flex-col items-center justify-center rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-8 text-center">
              <Lock size={32} className="text-amber-500" />
              <h3 className="mt-3 font-bold text-amber-900">Contenido exclusivo</h3>
              <p className="mt-2 text-sm text-amber-700">
                Inicia sesión para ver el perfil completo de cada empresa: redes sociales, dirección, contacto y vacantes activas.
              </p>
              <Link
                to="/login"
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
              >
                Iniciar sesión <ArrowRight size={15} />
              </Link>
              <Link
                to="/registro"
                className="mt-2 text-xs font-medium text-amber-700 hover:underline"
              >
                ¿No tienes cuenta? Regístrate gratis
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Empresas
